import { Menu } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuoteDrawer } from '@/contexts/QuoteDrawerContext';
import { useSettings } from '@/contexts/SettingsContext';
import DesktopNavigation from './DesktopNavigation';
import MegaMenuDropdown from './MegaMenuDropdown';
import MobileMenu from './MobileMenu';
import { capabilityMenu } from './navigationData';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeCapability, setActiveCapability] = useState(capabilityMenu[0].title);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);
  const mobileMenuTriggerRef = useRef(null);
  const location = useLocation();
  const settings = useSettings();
  const { openDrawer } = useQuoteDrawer();
  const transparent = location.pathname === '/' && !scrolled && !mobileOpen;
  const lightHeader = !transparent;

  useEffect(() => { const update = () => setScrolled(window.scrollY > 18); update(); window.addEventListener('scroll', update, { passive: true }); return () => window.removeEventListener('scroll', update); }, []);
  useEffect(() => { setActiveMenu(null); setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const priorOverflow = document.body.style.overflow; const priorPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden'; document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => { document.body.style.overflow = priorOverflow; document.body.style.paddingRight = priorPadding; };
  }, [mobileOpen]);
  const closeMobileMenu = useCallback((restoreFocus = false) => {
    setMobileOpen(false);
    if (restoreFocus) window.setTimeout(() => mobileMenuTriggerRef.current?.focus(), 0);
  }, []);
  useEffect(() => { const closeOnEscape = (event) => { if (event.key === 'Escape') { setActiveMenu(null); closeMobileMenu(true); } }; document.addEventListener('keydown', closeOnEscape); return () => document.removeEventListener('keydown', closeOnEscape); }, [closeMobileMenu]);
  const openMenu = () => { window.clearTimeout(closeTimer.current); setActiveMenu('CAPABILITIES'); };
  const scheduleClose = () => { closeTimer.current = window.setTimeout(() => setActiveMenu(null), 150); };
  const closeMenu = () => { window.clearTimeout(closeTimer.current); setActiveMenu(null); };
  const logo = transparent ? (settings.logo_light_url || '/logo-light.png') : (settings.logo_dark_url || '/logo-dark.png');

  return <>
    <header className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-200 ${transparent ? 'border-transparent bg-transparent' : 'border-hairline bg-arch-white'}`} data-testid="global-header">
      <div className="pr-container-wide flex h-[76px] items-center justify-between xl:h-[84px]">
        <Link to="/" className="flex min-h-[44px] items-center" data-testid="header-logo-link"><img src={logo} alt="PRASTARA" className="h-7 w-auto xl:h-8" data-testid="header-logo-image" /></Link>
        <DesktopNavigation isLight={lightHeader} activeMenu={activeMenu} onOpen={openMenu} onClose={closeMenu} />
        <div className="hidden items-center xl:flex"><button type="button" onClick={() => openDrawer({ source: 'header-send-drawings' })} className={`pr-cta min-h-[42px] px-4 py-2.5 ${transparent ? 'pr-cta-light' : 'pr-cta-primary'}`} data-testid="header-send-drawings-cta">Send Drawings <span aria-hidden="true">→</span></button></div>
        <button ref={mobileMenuTriggerRef} type="button" onClick={() => setMobileOpen(true)} className={`inline-flex h-11 w-11 items-center justify-center xl:hidden ${transparent ? 'text-arch-white' : 'text-soft-black'}`} aria-label="Open navigation" aria-expanded={mobileOpen} data-testid="mobile-navigation-open"><Menu size={25} aria-hidden="true" /></button>
      </div>
    </header>
    {activeMenu === 'CAPABILITIES' && <MegaMenuDropdown activeCapability={activeCapability} onCapabilityChange={setActiveCapability} onClose={closeMenu} onMouseEnter={openMenu} onMouseLeave={scheduleClose} />}
    <MobileMenu isOpen={mobileOpen} onClose={closeMobileMenu} settings={settings} openDrawer={openDrawer} />
  </>;
}